import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  DataTable,
  Badge,
  Button,
  InlineStack,
  Text,
  Pagination,
  SkeletonBodyText,
  EmptyState,
  Box,
  Banner,
} from '@shopify/polaris';
import { rentalsApi } from '@/services';
import type { Rental, RentalStatus } from '@/types';
import { orderNumber } from '@/utils/orderNumber';

export default function TodaysSendOrders() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const limit = 20;

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Everything due out: send date today or earlier, still unshipped.
  //
  // This used to ask for status='ready_to_ship' with no date filter at all, so it
  // listed every ready-to-ship order there had ever been, under a heading showing
  // today's date. Orders with no send date — everything migrated from the legacy
  // portal — are excluded by the date bound, which is what keeps hundreds of
  // abandoned 2020-era records out of the queue.
  const { data, isLoading, error } = useQuery({
    queryKey: ['todays-send-orders', page],
    queryFn: () =>
      rentalsApi.list({
        page,
        limit,
        dueForDispatch: true,
      }),
  });

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  /** Due before today and still sitting here — should have gone already. */
  const isOverdue = (rental: Rental) =>
    !!rental.sendDate && new Date(rental.sendDate) < startOfToday;

  const overdueCount = (data?.data || []).filter(isOverdue).length;

  const getStatusBadge = (rentalStatus: RentalStatus) => {
    const statusConfig: Record<string, { tone: 'success' | 'warning' | 'critical' | 'info' | undefined; label: string }> = {
      ready_to_ship: { tone: 'info', label: 'Ready to Ship' },
      processing: { tone: 'info', label: 'Processing' },
    };
    const config = statusConfig[rentalStatus] || { tone: undefined, label: rentalStatus };
    return <Badge tone={config.tone}>{config.label}</Badge>;
  };

  const rows = (data?.data || []).map((rental: Rental) => [
    <Button key={rental.id} variant="plain" onClick={() => navigate(`/rentals/${rental.id}`)}>
      {orderNumber(rental)}
    </Button>,
    rental.patientName || 'N/A',
    rental.patientEmail || 'N/A',
    rental.clinic?.name || 'Direct',
    // Due-out date, with anything already past flagged rather than sitting in the
    // list looking like everything else.
    isOverdue(rental) ? (
      <Badge key={`send-${rental.id}`} tone="critical">
        {`${new Date(rental.sendDate!).toLocaleDateString()} — overdue`}
      </Badge>
    ) : (
      <span key={`send-${rental.id}`}>
        {rental.sendDate ? new Date(rental.sendDate).toLocaleDateString() : '—'}
      </span>
    ),
    rental.rentalStartDate ? new Date(rental.rentalStartDate).toLocaleDateString() : 'N/A',
    `$${Number(rental.totalAmount).toFixed(2)}`,
    getStatusBadge(rental.rentalStatus),
    <Button key={`dispatch-${rental.id}`} onClick={() => navigate(`/warehouse/dispatch/${rental.id}`)}>
      Dispatch
    </Button>,
  ]);

  if (error) {
    return (
      <Page title="Going Out Today" backAction={{ onAction: () => navigate('/warehouse') }}>
        <Card>
          <EmptyState
            heading="Error loading dispatch queue"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Please try again later.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  return (
    <Page title="Going Out Today" backAction={{ onAction: () => navigate('/warehouse') }}>
      <Box paddingBlockEnd="400">
        <Banner tone="info">
          <p>
            Due to go out by {today}
            {overdueCount > 0 && (
              <>
                {' — '}
                <strong>{overdueCount} of these should already have shipped</strong>
              </>
            )}
            .
          </p>
        </Banner>
      </Box>

      <Card padding="0">
        {isLoading ? (
          <Box padding="400">
            <SkeletonBodyText lines={10} />
          </Box>
        ) : rows.length === 0 ? (
          <Box padding="800">
            <EmptyState
              heading="No orders to dispatch today"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>
                Nothing is due out today. Orders appear here on their send date, which is
                set when the order is placed and can be changed on the order.
              </p>
            </EmptyState>
          </Box>
        ) : (
          <>
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text']}
              headings={['Order', 'Customer', 'Email', 'Clinic', 'Send Date', 'Start Date', 'Total', 'Status', 'Action']}
              rows={rows}
            />
            {data && data.meta.totalPages > 1 && (
              <Box padding="400">
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    hasNext={page < data.meta.totalPages}
                    onPrevious={() => setPage((p) => p - 1)}
                    onNext={() => setPage((p) => p + 1)}
                  />
                </InlineStack>
              </Box>
            )}
          </>
        )}
      </Card>

      {data && (
        <Box paddingBlockStart="400">
          <Text as="p" tone="subdued" alignment="center">
            {data.meta.total} order{data.meta.total !== 1 ? 's' : ''} due out
          </Text>
        </Box>
      )}
    </Page>
  );
}
